import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Plus, Loader2, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate as formatAppDate } from '@/lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Funder Settings → Lawyer Lead Subscriptions section.
 * Allows a funder to subscribe to specific lawyers and get notified when those
 * lawyers validate new applications.
 */
export const LawyerSubscriptions = ({ getAuthHeaders }) => {
  const [data, setData] = useState({ subscriptions: [], active_count: 0, limit: 3, limit_reached: false });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [confirmUnsubId, setConfirmUnsubId] = useState(null);
  const debounceRef = useRef(null);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/funder/subscriptions`, { credentials: 'include', headers: getAuthHeaders() });
      if (r.ok) setData(await r.json());
    } catch (e) { console.error('Subscriptions load error:', e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  // Debounced lawyer search
  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); setShowDropdown(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API}/funder/lawyer-search?q=${encodeURIComponent(search.trim())}&limit=8`, { credentials: 'include', headers: getAuthHeaders() });
        if (r.ok) {
          const json = await r.json();
          // Filter out already-subscribed lawyers from the dropdown
          const subscribedIds = new Set(data.subscriptions.map(s => s.lawyer_id));
          setResults(json.filter(l => !subscribedIds.has(l.lawyer_id)));
          setShowDropdown(true);
        }
      } catch (e) { console.error('Lawyer search error:', e); }
      finally { setSearching(false); }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, data.subscriptions, getAuthHeaders]);

  const handleSubscribe = async (lawyer) => {
    if (data.limit_reached) {
      toast.error(`You have reached your current subscription limit of ${data.limit} lawyers.`);
      return;
    }
    try {
      const r = await fetch(`${API}/funder/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ lawyer_id: lawyer.lawyer_id }),
      });
      const json = await r.json();
      if (r.ok) {
        toast.success(json.message || 'Subscription added successfully.');
        setSearch('');
        setResults([]);
        setShowDropdown(false);
        fetchSubs();
      } else {
        toast.error(json.detail || 'Failed to subscribe');
      }
    } catch (e) { console.error('Subscribe error:', e); toast.error('Failed to subscribe'); }
  };

  const handleUnsubscribe = async (lawyerId) => {
    try {
      const r = await fetch(`${API}/funder/subscriptions/${lawyerId}`, {
        method: 'DELETE', credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        toast.success('Unsubscribed successfully.');
        setConfirmUnsubId(null);
        fetchSubs();
      } else {
        const json = await r.json().catch(() => ({}));
        toast.error(json.detail || 'Failed to unsubscribe');
      }
    } catch (e) { console.error('Unsubscribe error:', e); toast.error('Failed to unsubscribe'); }
  };

  const formatDate = (iso) => formatAppDate(iso) || '-';

  return (
    <div className="mcl-card mb-4" data-testid="lawyer-subscriptions-card">
      <div className="mcl-card-header">
        <h3 className="mcl-card-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Bookmark size={16} /> Lawyer Lead Subscriptions
        </h3>
      </div>
      <div className="mcl-card-body">
        <p style={{ color: 'var(--mcl-text-muted)', fontSize: '0.875rem', marginTop: 0 }}>
          Subscribe to specific lawyers and receive an email when they approve a new funding application.
        </p>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)' }} />
          <input
            className="mcl-form-input"
            placeholder="Search lawyer by name or firm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            disabled={data.limit_reached}
            style={{ paddingLeft: '34px' }}
            data-testid="lawyer-subscription-search"
          />
          {searching && <Loader2 size={14} className="animate-spin" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)' }} />}
          {showDropdown && results.length > 0 && (
            <div
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                marginTop: '4px', background: '#fff', border: '1px solid var(--mcl-border)',
                borderRadius: '8px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)',
                maxHeight: '260px', overflowY: 'auto',
              }}
              data-testid="lawyer-subscription-dropdown"
            >
              {results.map((lawyer) => (
                <button
                  key={lawyer.lawyer_id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSubscribe(lawyer); }}
                  style={{
                    width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', textAlign: 'left', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                  data-testid={`lawyer-option-${lawyer.lawyer_id}`}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{lawyer.lawyer_name || 'Unnamed'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lawyer.lawyer_firm || 'No firm listed'}</div>
                  </div>
                  <span style={{ color: 'var(--mcl-primary, #070689)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Plus size={12} /> Subscribe
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Limit indicator */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--mcl-text-muted)' }} data-testid="subscription-count">
            <strong style={{ color: 'var(--mcl-text-heading)' }}>{data.active_count}</strong> of <strong>{data.limit}</strong> active subscriptions
          </span>
          {data.subscription_plan && (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '999px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {data.subscription_plan} Plan
            </span>
          )}
        </div>

        {data.limit_reached && (
          <div style={{ padding: '10px 12px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: '0 6px 6px 0', color: '#92400e', fontSize: '0.85rem', marginBottom: '0.75rem' }} data-testid="subscription-limit-reached">
            You have reached your current subscription limit of {data.limit} lawyers.
          </div>
        )}

        <div style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)', marginBottom: '1rem' }} data-testid="subscription-future-message">
          Additional lawyer subscriptions may become available through future premium plans.
        </div>

        {/* Subscriptions list */}
        {loading ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--mcl-text-muted)' }}>Loading...</div>
        ) : data.subscriptions.length === 0 ? (
          <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--mcl-text-muted)', background: '#f8fafc', border: '1px dashed var(--mcl-border)', borderRadius: '8px' }} data-testid="no-subscriptions">
            You are not subscribed to any lawyers yet.
          </div>
        ) : (
          <div className="mcl-table-wrap">
            <table className="mcl-table" data-testid="subscriptions-table">
              <thead>
                <tr>
                  <th>Lawyer</th>
                  <th>Firm</th>
                  <th>Subscribed</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.map((s) => (
                  <tr key={s.subscription_id} data-testid={`subscription-row-${s.lawyer_id}`}>
                    <td style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{s.lawyer_name}</td>
                    <td>{s.lawyer_firm || <span style={{ color: 'var(--mcl-text-muted)' }}>—</span>}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>{formatDate(s.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {confirmUnsubId === s.lawyer_id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Are you sure?</span>
                          <button
                            type="button"
                            className="mcl-btn mcl-btn-sm mcl-btn-danger"
                            onClick={() => handleUnsubscribe(s.lawyer_id)}
                            data-testid={`confirm-unsubscribe-${s.lawyer_id}`}
                          >Yes</button>
                          <button
                            type="button"
                            className="mcl-btn mcl-btn-sm mcl-btn-ghost"
                            onClick={() => setConfirmUnsubId(null)}
                            data-testid={`cancel-unsubscribe-${s.lawyer_id}`}
                          >No</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="mcl-btn mcl-btn-sm mcl-btn-outline"
                          onClick={() => setConfirmUnsubId(s.lawyer_id)}
                          data-testid={`unsubscribe-${s.lawyer_id}`}
                        >
                          <X size={12} /> Unsubscribe
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawyerSubscriptions;
